import { CardContent, Grid, Pagination, Typography } from "@mui/material";
import { APP_PAGE_SIZE_DEFAULT } from "app/appConstants";
import { MprcCard, MprcCardActions, MprcIconButton, MprcRequestErrorDialog, MprcRequestErrorReport, MprcRequestIndicator, MprcSpinnerIndicator, MprcTextMuted } from "components";
import { MprcFilterCount } from "components/filter";
import { IconDelete } from "components/icons";
import { MprcConfirmDialog } from "components/MprcConfirmDialog";
import { MprcContainer } from "components/MprcContainer";
import { MprcListItemTitle } from "components/MprcListItemTitle";
import { ExtractErrorInformation, MprcRequestError } from "components/MprcRequestErrorReport";
import { KnowledgeQuickFilterType, ManagedQuickFilterResponse, ManagedQuickFiltersListResponse, ManagedQuickFiltersListResponsePaginatedList, ManagedQuickFilterTypeListResponse } from "generated/CoreApi_v1";
import { Formaters } from "lib/Formaters";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { EditQuickFilter } from "./EditQuickFilter";
import { Filter, ICustomQuickFilterOptions } from "./ListQuickFiltersFilter";

////////////////////////////////////////////////////////////////////////////////

interface IViewDataFilter {
    pageNumber: number;
    filter: ICustomQuickFilterOptions;
}

////////////////////////////////////////////////////////////////////////////////

function getEmptyViewFilter(): IViewDataFilter {
    return {
        pageNumber: 1,
        filter: {
            searchText: "",
            listExpired: false
        }
    };
}

function getEmptyRecord(): ManagedQuickFilterResponse {
    return {
        quickFilterId: "",
        filterType: KnowledgeQuickFilterType.CustomTopic,
        topic: [],
        channel: [],
        sticker: [],
        dueDate: null
    };
}

////////////////////////////////////////////////////////////////////////////////

export const ListQuickFilters: React.FC = (props) => {

    const api = useCoreApi();
    const { t } = useTranslation("knowledge");
    const { enqueueSnackbar } = useSnackbar();

    const [loading, setLoading] = useState<boolean>(true);
    const [viewError, setViewError] = useState<any>(null!);
    const [dialog, setDialog] = useState<any>();
    const [saving, setSave] = useState<ManagedQuickFilterResponse | null>(null);
    const [deletingId, setDeleteId] = useState<string | null>(null);
    const [editId, setEditId] = useState<string | null>(null);
    const [viewData, setViewData] = useState<ManagedQuickFiltersListResponsePaginatedList | null>(null);
    const [viewFilter, setViewFilter] = useState<IViewDataFilter>(getEmptyViewFilter());
    const filterTypes = useRef<ManagedQuickFilterTypeListResponse[]>([]);

    const filtered = viewFilter.filter.searchText.length > 0 || viewFilter.filter.listExpired == true;

    ////////////////////////////////////////////////////////////////////////////////

    ///
    const handleSave = (record: ManagedQuickFilterResponse) => {
        setSave(record);
        setDialog(null);
    };

    ///
    const handleAdd = () => {
        setDialog(<EditQuickFilter
            record={getEmptyRecord()}
            filterTypes={filterTypes.current}
            onClose={() => setDialog(undefined)}
            onConfirm={handleSave}
        />);
    };

    ///
    const handleFilterChange = (filter: ICustomQuickFilterOptions, reload: boolean) => {
        if (reload) {
            setViewFilter({ pageNumber: 1, filter });
            setLoading(true);
        }
        else setViewFilter({ ...viewFilter, filter });
    };

    ///
    const resetFilters = () => {
        setViewFilter(getEmptyViewFilter());
        setLoading(true);
    };

    ///
    const handleDelete = (record: ManagedQuickFiltersListResponse) => {
        setDialog(
            <MprcConfirmDialog
                open
                severity="danger"
                message={<span>{t("QuickFilter.List.ConfirmDeleteMessage")}</span>}
                onClose={(result) => {
                    if (result) {
                        setDeleteId(record.quickFilterId);
                    }
                    setDialog(null);
                }}
            />
        );
    };

    ///
    const RenderContent = () => (
        <MprcContainer>
            <MprcCard>
                <Filter
                    filterOptions={viewFilter.filter}
                    onAdd={handleAdd}
                    onFilterChange={handleFilterChange}
                />
                <CardContent>
                    {
                        loading
                            ? <MprcSpinnerIndicator />
                            : <>

                                {viewData && (
                                    <MprcContainer>
                                        <MprcFilterCount
                                            loading={loading}
                                            isEmpty={viewData.totalRows == 0}
                                            countText={t("QuickFilter.List.RecordsFoundText", { count: viewData.totalRows })}
                                            filter={{
                                                active: filtered,
                                                text: t("QuickFilter.List.RecordsFilteredText", { count: viewData.totalRows }),
                                                onResetFilter: resetFilters
                                            }}
                                        />
                                    </MprcContainer>
                                )}

                                {viewData && viewData.totalRows > 0 && (
                                    <Grid container justifyContent="center" alignItems="center" spacing={2}>
                                        {viewData.items.map((item, index) => {
                                            return (
                                                <Grid size={{ xs: 12 }} key={item.quickFilterId}>
                                                    <Grid container justifyContent="center" alignItems="center" spacing={2}>
                                                        <Grid size="grow">
                                                            <Grid container alignItems="center" spacing={1}>
                                                                <Grid>
                                                                    <MprcListItemTitle onClick={() => setEditId(item.quickFilterId)}>
                                                                        {item.name}
                                                                    </MprcListItemTitle>
                                                                </Grid>
                                                            </Grid>
                                                            <Grid>
                                                                <MprcTextMuted>
                                                                    <Typography variant="caption">
                                                                        {filterTypes.current.find(x => x.filterType == item.filterType)?.name}
                                                                    </Typography>
                                                                </MprcTextMuted>
                                                                {
                                                                    item.dueDate != null &&
                                                                    <MprcTextMuted>
                                                                        <Typography variant="caption">
                                                                            {` - ${t("QuickFilter.List.UntilText")} ${Formaters.localeDate(item.dueDate)}`}
                                                                        </Typography>
                                                                    </MprcTextMuted>
                                                                }
                                                            </Grid>
                                                        </Grid>
                                                        <Grid>
                                                            <MprcIconButton
                                                                size="small"
                                                                onClick={() => handleDelete(item)}
                                                            >
                                                                <IconDelete color="muted" />
                                                            </MprcIconButton>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                )
                                }
                            </>
                    }
                </CardContent>
                <MprcCardActions>
                    {viewData && viewData.totalPages > 1 && (
                        <Pagination
                            shape="rounded"
                            color="primary"
                            page={viewData.pageNumber}
                            count={viewData.totalPages}
                            onChange={(e, pageNumber) => {
                                if (pageNumber != viewFilter.pageNumber) {
                                    setViewFilter({ ...viewFilter, pageNumber });
                                    setLoading(true);
                                }
                            }}
                        />
                    )}
                </MprcCardActions>
            </MprcCard>
        </MprcContainer>
    );

    ////////////////////////////////////////////////////////////////////////////////
    /// EFFECT's
    ////////////////////////////////////////////////////////////////////////////////

    // Ao excluir
    useEffect(() => {
        async function postData() {
            try {
                if (deletingId) {
                    await api.v1.knowledgeQuickFilter.deleteQuickFilter({ quickFilterId: deletingId });

                    enqueueSnackbar(t("QuickFilter.List.DeleteSuccessMessage"), { variant: "success" });

                    if (viewData?.items.length == 1 && viewFilter.pageNumber > 1) setViewFilter(state => ({ ...state, pageNumber: state.pageNumber - 1 }));

                    setLoading(true);
                }
            } catch (e) {
                let error = ExtractErrorInformation(e as MprcRequestError);
                enqueueSnackbar(error.message, { variant: "error" });
            }
            finally {
                setDeleteId(null);
            }
        }

        postData();

    }, [deletingId]);

    // Ao carregar a lista
    useEffect(() => {
        async function getData() {
            try {
                if (filterTypes.current.length == 0) {
                    const quickFilterTypes = await api.v1.knowledgeQuickFilter.getQuickFilterTypes();
                    filterTypes.current = quickFilterTypes.data;
                }

                const result = await api.v1.knowledgeQuickFilter.managedQuickFilter({
                    pageNumber: viewFilter.pageNumber,
                    pageSize: APP_PAGE_SIZE_DEFAULT,
                    searchText: viewFilter.filter.searchText,
                    listExpired: viewFilter.filter.listExpired!
                });

                setViewData(result.data);
            }
            catch (e) {
                setViewError(e);
            }
            finally {
                setLoading(false);
            }
        }

        if (loading) getData();

    }, [loading]);

    // Ao editar
    useEffect(() => {
        async function getData() {
            try {
                const result = await api.v1.knowledgeQuickFilter.getQuickFilter({ quickFilterId: editId! });
                setDialog(
                    <EditQuickFilter
                        record={result.data}
                        filterTypes={filterTypes.current}
                        onClose={() => {
                            setDialog(undefined);
                        }}
                        onConfirm={handleSave}
                    />
                );
            }
            catch (e) {
                setViewError(e);
            }
            finally {
                setEditId(null);
            }
        }

        if (editId) getData();

    }, [editId]);

    // Ao salvar
    useEffect(() => {
        async function postData() {
            try {
                if (saving?.quickFilterId == "") {
                    await api.v1.knowledgeQuickFilter.createQuickFilter({
                        filterType: saving!.filterType,
                        dueDate: saving?.dueDate,
                        itemId: saving!.filterType == KnowledgeQuickFilterType.CustomTopic
                            ? saving!.topic[0].key
                            : saving!.filterType == KnowledgeQuickFilterType.CustomSticker
                                ? saving!.sticker[0].key
                                : saving!.channel[0].key
                    });
                }
                else {
                    await api.v1.knowledgeQuickFilter.updateQuickFilter({
                        quickFilterId: saving?.quickFilterId!,
                        dueDate: saving?.dueDate ?? null
                    });
                }

                setLoading(true);
            }
            catch (e) {
                setViewError(e);
            }
            finally {
                setSave(null);
            }
        }

        if (saving) postData();

    }, [saving]);

    ////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////

    return (
        <>
            {dialog}

            <MprcRequestIndicator visible={editId != null || saving != null} />

            {
                viewError != null && editId == null
                    ? <MprcRequestErrorReport error={viewError} />
                    : viewError != null && editId != null
                        ? <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(undefined)} />
                        : RenderContent()
            }
        </>
    );
};
